import { format, isToday, isYesterday, differenceInMinutes } from 'date-fns';

/**
 * Groups messages by time and sender for a more modern chat appearance
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Array of grouped message objects with metadata
 */
export const groupMessages = (messages) => {
  if (!messages || messages.length === 0) return [];

  const sortedMessages = [...messages].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );

  const grouped = [];
  let currentGroup = null;

  sortedMessages.forEach((message, index) => {
    const previousMessage = sortedMessages[index - 1];
    const nextMessage = sortedMessages[index + 1];
    
    const isNewGroup = !previousMessage || 
      previousMessage.author_id !== message.author_id ||
      differenceInMinutes(new Date(message.created_at), new Date(previousMessage.created_at)) > 5;

    const isLastInGroup = !nextMessage || 
      nextMessage.author_id !== message.author_id ||
      differenceInMinutes(new Date(nextMessage.created_at), new Date(message.created_at)) > 5;

    // Add timestamp separator if it's a new day or significant time gap
    if (shouldShowTimestamp(message, previousMessage)) {
      grouped.push({
        type: 'timestamp',
        id: `timestamp-${message.id}`,
        timestamp: message.created_at,
        content: formatTimestamp(message.created_at)
      });
    }

    // Add the message with grouping metadata
    grouped.push({
      ...message,
      type: 'message',
      isNewGroup,
      isLastInGroup,
      showAvatar: isNewGroup,
      showTimestamp: isLastInGroup,
      previousMessage,
      nextMessage
    });
  });

  return grouped;
};

/**
 * Determines if a timestamp separator should be shown
 * @param {Object} currentMessage 
 * @param {Object} previousMessage 
 * @returns {boolean}
 */
const shouldShowTimestamp = (currentMessage, previousMessage) => {
  if (!previousMessage) return true;
  
  const currentDate = new Date(currentMessage.created_at);
  const previousDate = new Date(previousMessage.created_at);
  
  // Show if different day
  if (currentDate.toDateString() !== previousDate.toDateString()) {
    return true;
  }
  
  // Show if more than 1 hour gap
  if (differenceInMinutes(currentDate, previousDate) > 60) {
    return true;
  }
  
  return false;
};

/**
 * Formats timestamp for display
 * @param {Date} date 
 * @returns {string}
 */
const formatTimestamp = (date) => {
  const messageDate = new Date(date);
  
  if (isToday(messageDate)) {
    return format(messageDate, 'HH:mm');
  }
  
  if (isYesterday(messageDate)) {
    return 'Yesterday';
  }
  
  return format(messageDate, 'MMM dd, yyyy');
};

/**
 * Groups messages by conversation thread (parent-child relationships)
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Array of threaded message objects
 */
export const organizeMessageThreads = (messages) => {
  if (!messages || messages.length === 0) return [];

  const messageMap = new Map();
  const rootMessages = [];

  // First pass: create message map and identify root messages
  messages.forEach(message => {
    messageMap.set(message.id, { ...message, replies: [] });
    
    if (!message.parent_id) {
      rootMessages.push(message.id);
    }
  });

  // Second pass: build the tree structure
  messages.forEach(message => {
    if (message.parent_id && messageMap.has(message.parent_id)) {
      const parent = messageMap.get(message.parent_id);
      const child = messageMap.get(message.id);
      
      // Add parent reference to child
      child.parentMessage = parent;
      
      // Add child to parent's replies
      parent.replies.push(child);
    }
  });

  // Return root messages with their reply trees
  return rootMessages.map(id => messageMap.get(id));
};

/**
 * Flattens threaded messages for display while maintaining thread context
 * @param {Array} threadedMessages 
 * @returns {Array}
 */
export const flattenMessageThreads = (threadedMessages) => {
  const flattened = [];
  
  const processMessage = (message, level = 0, parentChain = []) => {
    // Add the main message
    flattened.push({
      ...message,
      threadLevel: level,
      parentChain: [...parentChain],
      isThreaded: level > 0
    });
    
    // Process replies
    if (message.replies && message.replies.length > 0) {
      message.replies
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .forEach(reply => {
          processMessage(reply, level + 1, [...parentChain, message.id]);
        });
    }
  };
  
  threadedMessages.forEach(message => processMessage(message));
  
  return flattened;
};

/**
 * Checks if two messages should be visually grouped together
 * @param {Object} current 
 * @param {Object} previous 
 * @returns {boolean}
 */
export const shouldGroupWithPrevious = (current, previous) => {
  if (!previous || !current) return false;
  
  return (
    previous.author_id === current.author_id &&
    differenceInMinutes(new Date(current.created_at), new Date(previous.created_at)) <= 5 &&
    previous.type === 'message' &&
    current.type === 'message'
  );
};

export default {
  groupMessages,
  organizeMessageThreads,
  flattenMessageThreads,
  shouldGroupWithPrevious
};